import * as React from 'react';
import { InView } from 'react-intersection-observer';
import { FormattedMessage } from 'react-intl';

import { GetItemDataByIdQuery } from '@notino/shared/definitions/types';

import { useWishlistLists } from '@components/Universals/WishlistModifier/useWishlistLists';
import {
  FILTERS_MIN_PRODUCTS_COUNT,
  GTM_WISHLIST_LIST_NAME,
} from '@containers/WishList/constants';
import { useRemoveFromWishlistUndoNotification } from '@containers/WishList/hooks/useRemoveFromWishlistUndoNotification';
import { useWishlistId } from '@containers/WishList/hooks/useWishlistId';
import messages from '@containers/WishList/messages';
import { useWishlistStore } from '@containers/WishList/store';
import { trackWishlistPageViewEvent } from '@containers/wishlistShared/tracking';
import {
  useTrackToCartAsync,
  useTrackToCartFailedAsync,
} from '@helpers/googleTagManager/vpProductCartTracking';
import { useElementScrollDistanceFromTopThreshold } from '@hooks/useElementScrollDistanceFromTopThreshold';

import AnimatedProductList from '../AnimatedProductList';
import { SelectionMode } from '../SelectionMode';

import { FiltersSection } from './FiltersSection';
import { Divider, ProductsCount, StickyWrapper } from './styled';
import { useWishlistViewItemListTracking } from './tracking/useWishlistViewItemListTracking';
import { WishlistHeader } from './WishlistHeader';
import { Empty } from './WishlistHeader/Empty';

export const WishlistPage = () => {
  const products = useWishlistStore((state) => state.products);
  const hasMore = useWishlistStore((state) => state.hasMore);
  const productIds = useWishlistStore((state) => state.productIds);
  const isShared = useWishlistStore((state) => state.isSharedWishlist);
  const isEmpty = useWishlistStore((state) => state.totalProductCount === 0);
  const { listId } = useWishlistId();
  const totalFilteredProductCount = useWishlistStore(
    (state) => state.totalFilteredProductCount
  );
  const totalProductCount = useWishlistStore(
    (state) => state.totalProductCount
  );
  const loading = useWishlistStore((state) => state.isLoading);
  const { setProductWishlisted, fetchProducts } = useWishlistStore(
    (state) => state.actions
  );

  const trackAddToCartAsync = useTrackToCartAsync(
    productIds,
    GTM_WISHLIST_LIST_NAME
  );
  const trackAddToCartFailedAsync = useTrackToCartFailedAsync(
    productIds,
    GTM_WISHLIST_LIST_NAME
  );

  const { notifyProductRemoved } = useRemoveFromWishlistUndoNotification();

  const isDefaultList = listId === useWishlistLists.DEFAULT_LIST_ID;

  const handleProductAdded = async (
    product: GetItemDataByIdQuery['vpProductById']
  ) => {
    await trackAddToCartAsync(product!);
  };

  const trackItemView = useWishlistViewItemListTracking();

  const stickyRef = React.useRef<HTMLDivElement>(null);
  const isScrolled = useElementScrollDistanceFromTopThreshold({
    ref: stickyRef,
    threshold: 0,
  });

  return (
    <>
      <StickyWrapper ref={stickyRef}>
        <WishlistHeader isShared={isShared} />

        {isEmpty && <Empty isDefaultList={isDefaultList} />}

        {!isEmpty && totalProductCount >= FILTERS_MIN_PRODUCTS_COUNT && (
          <FiltersSection />
        )}

        {!isEmpty && (
          <Divider
            isScrolled={isScrolled}
            hideOnDesktop={totalProductCount < FILTERS_MIN_PRODUCTS_COUNT}
          />
        )}
      </StickyWrapper>

      {!isEmpty && (
        <ProductsCount>
          <span>
            <FormattedMessage {...messages.productsCount} />:
          </span>
          {totalFilteredProductCount}
        </ProductsCount>
      )}

      <AnimatedProductList
        isLoading={loading}
        isSharedWishlist={isShared}
        disableAnimation={loading}
        products={products}
        showAddToCartModal={false}
        productItemProps={{
          GTMEventLabel: GTM_WISHLIST_LIST_NAME,
          sendGTM: true,
          noPriceIfUnavailable: true,
          showBuyButton: true,
          onProductAdded: handleProductAdded,
          onProductAddFailed: trackAddToCartFailedAsync,
          onClosingCartModal: trackWishlistPageViewEvent,
          setProductWishlisted: (productId, wishlisted) => {
            if (!isShared) {
              notifyProductRemoved({
                product: products.find((p) => p?.id === productId)!,
                listId: listId!,
              });
            }
            setProductWishlisted(productId, wishlisted);
          },
          inSectionOfType: 'wishlist',
          wishlistActionLocation: 'wishlist',
          listIdForWishlistOperations: isShared ? undefined : listId,
        }}
        onProductInView={trackItemView}
      />

      {hasMore && !loading && (
        <InView
          rootMargin="0px 0px 100px 0px"
          onChange={(inView) =>
            inView &&
            fetchProducts({
              offset: productIds.length,
            })
          }
        />
      )}

      <SelectionMode />
    </>
  );
};
